import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil, throttleTime } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';

import { THEME } from '@lib/ui/theme';

import { FsLvtSounds } from '../../models/lvt-sounds';
import { FsLvtLights } from '../../models/lvt-lights';
import { SnLvtService } from '../../services/lvt.service';
import { FsLvtToken } from '../../models/lvt-token';

@Component({
  selector: 'fs-lvt-feed-card',
  templateUrl: './lvt-feed-card.component.html',
  styleUrls: ['./lvt-feed-card.component.scss'],
})
export class LvtFeedCardComponent implements OnInit, OnDestroy {
  get mouseWheel$(): Observable<any> {
    return this._mouseWheel$.asObservable();
  }

  public timer;
  public checkin;
  public streamId: any;
  public signalUrl: any;
  public streamInfo: any;
  public refreshInterval: any;
  public isFullscreen: boolean;
  public streamStarted: boolean = false;
  public isPlaying: boolean;
  public canvas: any;
  public ptzOverlay: boolean;
  public moveMultiplier: number = 5;
  public startPtzMovement: boolean = false;
  public isCrosshairEnabled = false;
  public isMuted = true;
  public showCrosshair: boolean = false;
  public togglePtz: boolean = true;
  public isDragging: boolean = false;
  public videoRatio: number;
  public centerX: number;
  public centerY: number;
  public linesToDraw: any[] = [];
  public movementType: string = 'relative';
  public xPos;
  public yPos;
  public dragX = 0;
  public dragY = 0;
  public createdLine: any;
  public distX = 0;
  public distY = 0;
  public amount: number = 0;

  @Input() public lvt: any;
  @Input() public headers: any;
  @Input() public soundList: FsLvtSounds[];
  @Input() public lightList: FsLvtLights[];
  @Input() public isOnMap: boolean;

  private readonly _destroy$ = new Subject<void>();
  private _fullscreenCallback: (event: any) => void;
  private readonly _mouseWheel$ = new Subject<any>();

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _elementRef: ElementRef,
    private readonly _lvtService: SnLvtService,
    private readonly _cd: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    if (!this.isOnMap) {
      this._lvtService
        .getLvtCameras()
        .pipe(takeUntil(this._destroy$))
        .subscribe((response) => {
          const id = this.lvt?.externalCameraId.split('__')[1];
          const lvtCamera = response.results.filter((item) => item.cameraId === id);
          this.lvt = lvtCamera[0];
          this._cd.detectChanges();
          if (this.lvt?.liveUnitId && this.lvt?.accountName) {
            this._lvtService
              .getLvtSounds(this.lvt.liveUnitId, this.lvt.accountName)
              .pipe(takeUntil(this._destroy$))
              .subscribe((value: FsLvtSounds[]) => {
                this.soundList = value;
              });
            this._lvtService
              .getLvtLights(this.lvt.liveUnitId, this.lvt.accountName)
              .pipe(takeUntil(this._destroy$))
              .subscribe((value: FsLvtLights[]) => {
                this.lightList = value;
              });
            this._lvtService
              .getToken(this.lvt.accountName)
              .pipe(takeUntil(this._destroy$))
              .subscribe((value: FsLvtToken) => {
                this.headers = { Authorization: `Bearer ${value.accessToken}` };
                this.startStream(this.headers);
              });
          }
        });
    } else {
      this.startStream(this.headers);
    }
    this.mouseWheel$.pipe(throttleTime(300), takeUntil(this._destroy$)).subscribe((event) => {
      this._handlePtzScroll(event);
    });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    clearInterval(this.timer);
    clearInterval(this.checkin);
  }

  public playSound(item: FsLvtSounds): void {
    if (!this.lvt?.liveUnitId || !this.lvt?.accountName) {
      return;
    }
    this._lvtService
      .postLvtSounds(this.lvt.liveUnitId, this.lvt.accountName, item.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe();
  }

  public playLight(item: FsLvtLights): void {
    if (!this.lvt?.liveUnitId || !this.lvt?.accountName) {
      return;
    }
    const state = item.state === 'off' ? 'on' : 'off';
    this._lvtService
      .postLvtLights(this.lvt.liveUnitId, this.lvt.accountName, item.id, { state })
      .pipe(takeUntil(this._destroy$))
      .subscribe();
  }

  public startStream(headers: any): void {
    this._httpClient
      .post(
        `https://api.lvt.com/v1/cameras/${this.lvt.cameraId}/streams/`,
        { protocol: 'webrtc' },
        { headers },
      )
      .pipe(takeUntil(this._destroy$))
      .subscribe((res: any) => {
        this.streamId = res.streamId;
        this.signalUrl = res.signalUrl;
        this.streamInfo = res.streamInfo;
        this.refreshInterval = res.refreshInterval;
        this.checkin = setInterval(() => this._checkIn(), this.refreshInterval);
        this.establishConnectionToStream();
      });
  }

  public establishConnectionToStream(): void {
    const ws = new WebSocket(this.signalUrl);
    const peerConnection = new RTCPeerConnection();
    const getOffer = () => {
      const message = {
        direction: 'play',
        command: 'getOffer',
        streamInfo: this.streamInfo,
      };
      ws.send(JSON.stringify(message));
    };

    ws.addEventListener('open', () => {
      getOffer();
    });

    ws.addEventListener('message', (message) => {
      const data = JSON.parse(message.data);
      const status = Number(data.status);
      if (status === 502 || status === 504) {
        setTimeout(getOffer, 1000);
      }
      if (status === 200) {
        if (data.streamInfo && data.streamInfo.sessionId) {
          this.streamInfo.sessionId = data.streamInfo.sessionId;
        }
        if (data.sdp) {
          peerConnection
            .setRemoteDescription(new RTCSessionDescription(data.sdp))
            .then(() => peerConnection.createAnswer())
            .then((description) => peerConnection.setLocalDescription(description))
            .then(() => {
              const mes = {
                direction: 'play',
                command: 'sendResponse',
                sdp: peerConnection.localDescription,
                streamInfo: this.streamInfo,
              };

              ws.send(JSON.stringify(mes));
            });
        }
        if (data.iceCandidates) {
          for (const ic of data.iceCandidates) {
            if (ic.candidate.indexOf(' UDP ') > 0) {
              peerConnection.addIceCandidate(new RTCIceCandidate(ic));
            }
          }
        }
      }
    });
    peerConnection.addEventListener('track', (event) => {
      const [stream] = event.streams;
      const videoEm: HTMLVideoElement = this._elementRef.nativeElement.querySelector(
        `#webrtc-video-${this.lvt.cameraId}`,
      );
      videoEm.srcObject = stream;
      this.streamStarted = true;
      const container = document.getElementById(`video-container-${this.lvt.cameraId}`);
      this._fullscreenCallback = () => this._handleFullscreenChange();
      container.addEventListener('fullscreenchange', this._fullscreenCallback, true);
      this._cd.detectChanges();
    });
  }

  public muteButton(): void {
    const video = this._elementRef.nativeElement.querySelector(
      `#webrtc-video-${this.lvt.cameraId}`,
    );
    video.muted = !video.muted;
    this.isMuted = !this.isMuted;
    this._cd.detectChanges();
  }

  public playButton(): void {
    const video = this._elementRef.nativeElement.querySelector(
      `#webrtc-video-${this.lvt.cameraId}`,
    );
    if (video.paused) {
      video.play();
      this.isPlaying = true;
    } else {
      video.pause();
      this.isPlaying = false;
    }
    this._cd.detectChanges();
  }

  public fullscreenButton(): void {
    const container = document.getElementById(`video-container-${this.lvt.cameraId}`);
    const fullscreenApi = container.requestFullscreen;
    if (!document.fullscreenElement) {
      fullscreenApi.call(container);
      this.isFullscreen = true;
    } else {
      document.exitFullscreen();
      this.isFullscreen = false;
    }
    this._cd.detectChanges();
    setTimeout(() => {
      this._togglePtzOverlay(this.isFullscreen);
    }, 1000);
  }

  public ptzZoomControl(zoomAmount: number): void {
    this._lvtService
      .patchLvtCamPosition(this.lvt.cameraId, this.lvt.accountName, { zoom: zoomAmount })
      .pipe(takeUntil(this._destroy$))
      .subscribe();
  }

  public showCanvas(): void {
    this.showCrosshair = true;
    this._cd.detectChanges();
  }

  public hideCanvas(): void {
    this.showCrosshair = false;
    this._cd.detectChanges();
  }

  public handleRightClicks(event: MouseEvent): void {
    event.preventDefault();
  }

  public showDragControls(): boolean {
    return this.lvt?.capabilities?.position && this.streamStarted && this.isFullscreen;
  }

  public startAreaZoomDrawing(event: any): void {
    return;
  }

  public centerOnClick(event: any): void {
    return;
  }

  public onScrollZoom(event: any): void {
    if (!this.showDragControls()) {
      return;
    }

    this._mouseWheel$.next(event);
  }

  public startDrawing(event: any): void {
    if (event.button !== 0) {
      return;
    }
    clearInterval(this.timer);
    this.isCrosshairEnabled = true;
    this.isDragging = true;
    this._cd.detectChanges();
    const borderWidth = 3;
    this.xPos = 0;
    this.yPos = 0;
    this.centerX =
      parseInt(this.canvas.offsetLeft, 10) + (parseInt(this.canvas.width, 10) + borderWidth) / 2;
    this.centerY =
      parseInt(this.canvas.offsetTop, 10) + (parseInt(this.canvas.height, 10) + borderWidth) / 2;
    this.createdLine = {
      type: 'line',
      x: this.centerX,
      y: this.centerY,
      w: 0,
      h: 0,
    };

    setTimeout(() => {
      this._lvtService
        .patchLvtCamPosition(this.lvt.cameraId, this.lvt.accountName, {
          tilt: this.yPos * 100,
          pan: this.xPos * 100,
        })
        .pipe(
          finalize(() => (this.isDragging = false)),
          takeUntil(this._destroy$),
        )
        .subscribe();
    }, 50);

    this.timer = setInterval(() => {
      this._lvtService
        .patchLvtCamPosition(this.lvt.cameraId, this.lvt.accountName, {
          tilt: this.yPos * 100,
          pan: this.xPos * 100,
        })
        .pipe(
          finalize(() => (this.isDragging = false)),
          takeUntil(this._destroy$),
        )
        .subscribe();
    }, 200);
    this.linesToDraw.push(this.createdLine);
  }

  public keepDrawing(event: any): void {
    if (this.createdLine && this.isCrosshairEnabled) {
      this.isDragging = true;
      this.createdLine.w = event.offsetX - this.createdLine.x;
      this.createdLine.h = event.offsetY - this.createdLine.y;

      this._updatePositions(event);
      this.linesToDraw.push(this.createdLine);
    }
  }

  public stopDrawing(): void {
    this.isDragging = false;
    this.createdLine = null;
    this.linesToDraw = [];
    clearInterval(this.timer);
    this._genCrosshairContext();
    setTimeout(() => {
      this.isCrosshairEnabled = false;
    }, 200);
    this.distX = 0;
    this.distY = 0;
  }

  private _handlePtzScroll(event: any): void {
    this.ptzZoomControl(event.deltaY);
  }

  private _handleFullscreenChange(): void {
    this.isFullscreen = !!document.fullscreenElement;
    this._cd.detectChanges();
  }

  private _togglePtzOverlay(on: boolean): void {
    this.ptzOverlay = on;
    this._cd.detectChanges();
    if (this.lvt?.capabilities?.position && this.streamStarted && this.isFullscreen) {
      const overlay = document.getElementById('ptzOverlay');
      const video = this._elementRef.nativeElement.querySelector(
        `#webrtc-video-${this.lvt.cameraId}`,
      );

      this.videoRatio = video.clientWidth / video.videoWidth;
      const newVideoHeight = video.videoHeight * this.videoRatio;
      if (overlay) {
        overlay.style.height = `${newVideoHeight}px`;
        overlay.style.width = `${video.clientWidth}px`;
        overlay.style.top = `calc(50% - ${newVideoHeight / 2}px)`;
        overlay.style.left = '0px';
        overlay.style.display = 'block';
      }

      this.canvas = document.getElementById('ptzCrosshair');
      if (this.canvas) {
        this.centerX = parseInt(this.canvas.offsetLeft, 10) + this.canvas.width / 2;
        this.centerY = parseInt(this.canvas.offsetTop, 10) + this.canvas.height / 2;
        this._genCrosshairContext();
        this.hideCanvas();
      }
    } else {
      const element = this._elementRef.nativeElement;
      const overlay = element.querySelector('#ptzOverlay');
      if (overlay) {
        overlay.style.display = 'none';
      }
    }
  }

  private _updatePositions(event: any): void {
    this.canvas.getContext('2d').clearRect(0, 0, this.canvas.width, this.canvas.height);
    const svg = this._elementRef.nativeElement.querySelector('.shapeCanvas');
    const rect = svg.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;
    const intervalX = offsetX - this.centerX;
    const intervalY = this.centerY - offsetY;
    this.xPos = intervalX / 7500;
    this.yPos = intervalY / 7500;
  }

  private _genCrosshairContext(): void {
    if (!this.canvas) {
      return;
    }
    const context = this.canvas.getContext('2d');
    let x = this.canvas.width / 2;
    let y = this.canvas.height / 2;
    x = Math.floor(x);
    y = Math.floor(y);
    context.lineWidth = 3;
    context.moveTo(x, y - 7);
    context.lineTo(x, y + 7);
    context.moveTo(x - 7, y);
    context.lineTo(x + 7, y);
    context.strokeStyle = THEME.success;
    context.stroke();
  }

  private _checkIn(): void {
    this._httpClient
      .post(
        `https://api.lvt.com/v1/streams/${this.streamId}:checkIn/`,
        {},
        { headers: this.headers },
      )
      .pipe(takeUntil(this._destroy$))
      .subscribe();
  }
}
